/** SUNTRONIX ONE — Pilot Control Room.
 *  Composes the operational health tiles the Pilot Playbook (§14)
 *  requires so pilot ops sees today's state at a glance without
 *  hopping between five different pages.
 *
 *  Backed by `GET /api/pilot/control-room?master_company_id=…` which
 *  aggregates open cases · needs-attention · comm failures · data
 *  issues · SLA risk · Day-1 risk · address issues · P0/P1 defects.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import {
  AlertTriangle, CheckCircle2, ChevronRight, Clock,
  Mail, MapPin, ShieldAlert, Activity, Users, Package,
} from "lucide-react";
import { toast } from "sonner";

const TILE_ICON = {
  open_cases: Package,
  needs_attention: AlertTriangle,
  communication_failures: Mail,
  data_issues: ShieldAlert,
  sla_risk: Clock,
  day1_risk: Users,
  address_issues: MapPin,
  p0_defects: ShieldAlert,
  p1_defects: AlertTriangle,
};

const TONE_CLASSES = {
  ok:   "border-emerald-200 bg-emerald-50/50 text-emerald-800",
  warn: "border-amber-200 bg-amber-50/60 text-amber-900",
  risk: "border-red-200 bg-red-50/60 text-red-900",
  info: "border-slate-200 bg-white text-slate-900",
};

export default function PilotControlRoom() {
  const [data, setData] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [mcId, setMcId] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (targetId) => {
    setLoading(true);
    try {
      const url = "/pilot/control-room" + (targetId ? `?master_company_id=${targetId}` : "");
      const { data } = await api.get(url);
      setData(data);
    } catch (e) {
      toast.error("Could not load pilot signals");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/master-clients/portfolio/summary");
        const list = (data?.customers || []).slice(0, 30);
        setCustomers(list);
        if (list.length) setMcId(current => current || list[0].id);
      } catch { /* ignore */ }
    })();
    // Initialise customer choices once; later selections remain under operator control.
  }, []);

  useEffect(() => { if (mcId) load(mcId); }, [mcId, load]);

  const totals = useMemo(() => data?.totals || {}, [data?.totals]);
  const rag = useMemo(() => {
    if (!data) return null;
    if (totals.signals_red > 0) return { label: "RED", tone: "risk" };
    if (totals.signals_amber > 0) return { label: "AMBER", tone: "warn" };
    return { label: "GREEN", tone: "ok" };
  }, [data, totals]);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="pilot-control-room">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.16em] text-slate-500">Pilot</div>
          <h1 className="mt-0.5 font-heading text-2xl sm:text-3xl text-slate-900 tracking-tight">
            Control Room
          </h1>
          <p className="mt-1 text-sm text-slate-600 max-w-2xl">
            Today&apos;s operational health across a pilot customer — every tile drills to the
            working list so ops can act, not decode.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            data-testid="pilot-control-customer-picker"
            value={mcId}
            onChange={(e) => setMcId(e.target.value)}
            className="rounded-md border border-slate-300 bg-white text-[13px] px-3 py-1.5"
          >
            {customers.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          {rag && (
            <span
              data-testid="pilot-control-rag"
              className={`text-[11px] uppercase tracking-[0.14em] px-2.5 py-1 rounded-full border ${TONE_CLASSES[rag.tone]}`}
            >
              {rag.label}
            </span>
          )}
        </div>
      </header>

      {loading && (
        <div className="text-[13px] text-slate-500" data-testid="pilot-control-loading">Loading pilot signals…</div>
      )}

      {data && (
        <>
          <section className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-3">
            {data.tiles.map((tile) => {
              const Icon = TILE_ICON[tile.key] || Activity;
              const cls = TONE_CLASSES[tile.tone] || TONE_CLASSES.info;
              return (
                <div
                  key={tile.key}
                  data-testid={`pilot-tile-${tile.key}`}
                  className={`rounded-lg border p-4 flex flex-col gap-1.5 transition ${cls}`}
                >
                  <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.14em]">
                    <span className="opacity-80">{tile.label}</span>
                    <Icon className="h-3.5 w-3.5 opacity-70" strokeWidth={1.6} />
                  </div>
                  <div className="text-3xl font-heading font-semibold leading-none">{tile.value}</div>
                  <a
                    href="#"
                    onClick={(e) => { e.preventDefault(); toast.info(`Drill → ${tile.drill_endpoint}`); }}
                    className="mt-1 text-[11px] inline-flex items-center gap-1 opacity-70 hover:opacity-100"
                  >
                    Drill to list <ChevronRight className="h-3 w-3" strokeWidth={1.6} />
                  </a>
                </div>
              );
            })}
          </section>

          <section className="rounded-lg border border-slate-200 bg-white p-4">
            <h2 className="text-sm font-medium text-slate-900 flex items-center gap-2 mb-3">
              <CheckCircle2 className="h-4 w-4 text-slate-500" strokeWidth={1.6} />
              Pilot exit criteria
            </h2>
            <ul className="grid md:grid-cols-2 gap-x-6 gap-y-1.5 text-[12.5px] text-slate-700">
              <li className={totals.signals_red === 0 ? "text-emerald-700" : "text-red-700"}>
                Zero open P0 signals — <b>{totals.signals_red === 0 ? "OK" : `${totals.signals_red} open`}</b>
              </li>
              <li className={totals.signals_amber === 0 ? "text-emerald-700" : "text-amber-800"}>
                Amber signals cleared — <b>{totals.signals_amber === 0 ? "OK" : `${totals.signals_amber} open`}</b>
              </li>
              <li>Warehouse inventory reconciles — check Data Quality</li>
              <li>Golden Asset history intact — no duplicate serials</li>
              <li>Address Verification working — Address issues tile above</li>
              <li>Department routing working — dm → ops → bo → store</li>
              <li>Customer communications working — Comm Failures tile above</li>
              <li>No mandatory side-channel for scoped work</li>
            </ul>
          </section>

          <div className="text-[11px] text-slate-500">
            As of {new Date(data.as_of).toLocaleString()} · scope:&nbsp;
            {mcId ? (customers.find((c) => c.id === mcId)?.name || mcId) : "all customers"}
          </div>
        </>
      )}
    </div>
  );
}
